import React, { useState } from 'react';
import { FieldRenderer } from './FormRegistryFields';
import { useModuleRegistryExtras } from './formRegistryStore';

function isScopeField(field) {
  return field.type === 'dropdown' && ['module', 'sub-module'].includes(field.id);
}

function FieldGroup({ title, fields, values, onChange }) {
  return (
    <div className="space-y-3">
      {title && <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{title}</p>}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {fields.map((field) => (
          <FieldRenderer
            key={field.id}
            field={field}
            value={values[field.id]}
            otherValue={values[field.otherFieldKey ?? `${field.id}_other`]}
            onFieldChange={onChange}
            onOtherChange={onChange}
          />
        ))}
      </div>
    </div>
  );
}

function RegistryPanel({ eyebrow, title, description, children }) {
  return (
    <section className="mb-5 rounded-2xl border border-green-100 bg-green-50/40 p-5 shadow-sm">
      <div className="mb-4">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-green-700">{eyebrow}</p>
        <h2 className="mt-1 text-base font-bold text-gray-900">{title}</h2>
        <p className="mt-1 text-xs text-gray-500">{description}</p>
      </div>
      <div className="space-y-5">{children}</div>
    </section>
  );
}

// Everything Form Registry adds to a module's Add Record page on top of its
// built-in fields:
//  • fields an admin added to the module's Standard form, and
//  • every Custom form bound to the same module / submodule.
// Updates live whenever the registry changes (same tab or another tab).
export default function RegistryCustomFields({ moduleKey, submoduleKey }) {
  const { standard, addedSections, customForms } = useModuleRegistryExtras(moduleKey, submoduleKey);
  const [values, setValues] = useState({});

  const updateValue = (fieldId, value) => {
    setValues((current) => ({ ...current, [fieldId]: value }));
  };

  const customPanels = customForms
    .map((form) => ({
      form,
      sections: (form.sections ?? [])
        .map((section) => ({ ...section, fields: (section.fields ?? []).filter((field) => !isScopeField(field)) }))
        .filter((section) => section.fields.length > 0),
    }))
    .filter((panel) => panel.sections.length > 0);

  if (addedSections.length === 0 && customPanels.length === 0) return null;

  return (
    <>
      {addedSections.length > 0 && (
        <RegistryPanel
          eyebrow="Additional Fields"
          title={standard?.name ?? 'Standard Form'}
          description="Fields added to this standard form in Form Registry."
        >
          {addedSections.map((section) => (
            <FieldGroup
              key={section.id}
              title={section.title}
              fields={section.fields}
              values={values}
              onChange={updateValue}
            />
          ))}
        </RegistryPanel>
      )}

      {customPanels.map(({ form, sections }) => (
        <RegistryPanel
          key={form.id}
          eyebrow="Custom Form"
          title={form.name}
          description="Additional fields configured in Form Registry."
        >
          {sections.map((section) => (
            <FieldGroup
              key={section.id}
              title={sections.length > 1 ? section.title : ''}
              fields={section.fields}
              values={values}
              onChange={updateValue}
            />
          ))}
        </RegistryPanel>
      ))}
    </>
  );
}
